import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ResponsiveService, ScreenSize } from '../../services/responsive.service';
import { ResponsiveLayoutComponent } from '../responsive-layout/responsive-layout.component';
import { ResponsiveClassDirective } from '../../directives/responsive-class.directive';

@Component({
  selector: 'app-responsive-demo',
  standalone: true,
  imports: [CommonModule, FormsModule, ResponsiveLayoutComponent, ResponsiveClassDirective],
  templateUrl: './responsive-demo.component.html',
  styleUrl: './responsive-demo.component.css'
})
export class ResponsiveDemoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  currentScreenSize: ScreenSize | null = null;
  
  // Demo configuration
  layoutConfig = {
    columns: 3,
    gap: '1rem',
    mobileColumns: 1,
    tabletColumns: 2,
    desktopColumns: 3,
    alignItems: 'stretch' as 'start' | 'center' | 'end' | 'stretch',
    justifyContent: 'start' as 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly'
  };

  // Demo data for testing
  demoItems = Array.from({ length: 12 }, (_, i) => ({
    id: i + 1,
    title: `Demo Item ${i + 1}`,
    description: `This is a sample description for item ${i + 1}. It demonstrates responsive layout behavior.`,
    type: ['primary', 'success', 'warning', 'info', 'secondary', 'danger'][i % 6]
  }));

  // Responsive feature tests
  features = [
    { name: 'Touch Device', check: () => this.responsiveService.isTouchDevice() },
    { name: 'High DPI', check: () => this.responsiveService.isHighDPI() },
    { name: 'Prefers Dark Mode', check: () => this.responsiveService.prefersDarkMode() },
    { name: 'Prefers Reduced Motion', check: () => this.responsiveService.prefersReducedMotion() },
    { name: 'Prefers High Contrast', check: () => this.responsiveService.prefersHighContrast() },
  ];

  constructor(private responsiveService: ResponsiveService) {}

  ngOnInit(): void {
    this.responsiveService.screenSize$
      .pipe(takeUntil(this.destroy$))
      .subscribe(screenSize => {
        this.currentScreenSize = screenSize;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Demo controls
  updateColumns(value: number): void {
    this.layoutConfig.columns = value;
    
    // Auto-adjust responsive columns based on main column count
    if (value <= 2) {
      this.layoutConfig.mobileColumns = 1;
      this.layoutConfig.tabletColumns = Math.min(2, value);
      this.layoutConfig.desktopColumns = value;
    } else {
      this.layoutConfig.mobileColumns = 1;
      this.layoutConfig.tabletColumns = 2;
      this.layoutConfig.desktopColumns = value;
    }
  }

  // Utility methods for template
  getBreakpointInfo(): any {
    if (!this.currentScreenSize) return {};
    
    return {
      current: this.responsiveService.getBreakpoint(),
      width: this.currentScreenSize.width,
      height: this.currentScreenSize.height,
      orientation: this.currentScreenSize.orientation,
      devicePixelRatio: this.currentScreenSize.devicePixelRatio
    };
  }

  getDeviceType(): string {
    if (!this.currentScreenSize) return 'Unknown';
    
    if (this.currentScreenSize.isMobile) return 'Mobile';
    if (this.currentScreenSize.isTablet) return 'Tablet';
    if (this.currentScreenSize.isDesktop) return 'Desktop';
    return 'Unknown';
  }

  getResponsiveClasses(): string[] {
    return this.responsiveService.getResponsiveClasses('demo-element');
  }

  getSafeAreaInfo(): any {
    return this.responsiveService.getSafeAreaInsets();
  }

  getViewportInfo(): any {
    return {
      height: this.responsiveService.getViewportHeight(),
      documentHeight: this.responsiveService.getDocumentHeight()
    };
  }

  testMediaQuery(query: string): boolean {
    return this.responsiveService.matchesMediaQuery(query);
  }

  // Demo actions
  simulateResize(): void {
    // This is just for demonstration - actual resize would be triggered by user
    console.log('Resize simulation - check browser dev tools and resize window');
  }

  lockScroll(): void {
    this.responsiveService.lockScroll();
    setTimeout(() => {
      this.responsiveService.unlockScroll();
    }, 3000);
  }

  exportLayoutInfo(): void {
    const info = {
      timestamp: new Date().toISOString(),
      screenSize: this.currentScreenSize,
      breakpoint: this.responsiveService.getBreakpoint(),
      layoutConfig: this.layoutConfig,
      features: this.features.map(f => ({ name: f.name, value: f.check() })),
      safeArea: this.getSafeAreaInfo(),
      viewport: this.getViewportInfo()
    };

    const blob = new Blob([JSON.stringify(info, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `responsive-layout-info-${Date.now()}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  // Color helper for demo items
  getItemClass(type: string): string {
    const typeMap: { [key: string]: string } = {
      'primary': 'bg-primary text-white',
      'success': 'bg-success text-white',
      'warning': 'bg-warning text-dark',
      'info': 'bg-info text-white',
      'secondary': 'bg-secondary text-white',
      'danger': 'bg-danger text-white'
    };
    return typeMap[type] || 'bg-light';
  }
}